import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CaretDownIcon, CaretUpIcon } from '@radix-ui/react-icons';
import {
  ColumnFiltersState,
  flexRender,
  SortingState,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  VisibilityState,
  ColumnDef,
} from '@tanstack/react-table';
import { DateTime } from 'luxon';
import Router, { useRouter } from 'next/router';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table';
import { prettyDate } from '@/utils/dates';
import { Burst, BurstStatus, ScoutLocation } from '@/types/location';
import { Checkbox } from '@/components/shadcn/checkbox';
import { useScout } from '@/hooks/useScout';
import { Button } from '@/components/shadcn/button';
import { Views } from '@/types/view';
import { capitalize } from '@/utils/strings';
import { isLatestBurstActive } from '@/utils/scout';
import { FiltersState } from '@/types/filter';
import { buildUrlParamString } from '@/utils/url';

const PAGE_SIZE = 100;

interface Props {
  locations: ScoutLocation[];
  selectionCallback?: (id: string | number, lastSelectedView: Views) => void;
  filters: FiltersState;
  isActiveView: boolean;
}

export default function List({
  locations,
  selectionCallback = () => {},
  filters,
  isActiveView,
}: Props) {
  const router = useRouter();
  const query = router.query;

  const sortingKey = (query.sortKey as string) ?? 'lastSeen';
  const sortingOrder = (query.sortOrder as string) === 'desc';
  const page = (query.page as string) ?? '0';
  const validPage =
    isNaN(Number(page)) || Number(page) < 1 ? 0 : Number(page) - 1; // this one offset is decorative to offset the 0-based index

  const [pageIndex, setPageIndex] = useState(validPage);
  const [sorting, setSorting] = useState<SortingState>([
    {
      id: sortingKey,
      desc: sortingOrder,
    },
  ]);
  const sortKey = sorting[0].id;
  const sortOrder = sorting[0].desc ? 'desc' : 'asc';

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const locationsLength = useRef<number | null>(locations.length);
  const selectAll = useRef<boolean>(false);

  const { selectedLocationIds, setSelectedLocationIds } = useScout();

  const updateSessionStorage = (ids: string[]) => {
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('scout-selected-locations', JSON.stringify(ids));
    }
  };

  const handleSelectedLocationIds = useCallback(
    (ids: string[]) => {
      setSelectedLocationIds(ids);
      updateSessionStorage(ids);
    },
    [setSelectedLocationIds],
  );

  const handleSelectAll = () => {
    selectAll.current = !selectAll.current;

    if (selectAll.current) {
      const selected = locations.filter(
        location => !isLatestBurstActive(location.bursts),
      );

      handleSelectedLocationIds(selected.map(location => location._id));
    } else {
      handleSelectedLocationIds([]);
    }
  };

  useEffect(() => {
    if (locationsLength.current === locations.length) {
      return;
    }

    locationsLength.current = locations.length;
    setPageIndex(0);

    if (selectAll.current) {
      const selected = locations.filter(
        location => !isLatestBurstActive(location.bursts),
      );

      handleSelectedLocationIds(selected.map(location => location._id));
    }
  }, [handleSelectedLocationIds, locations]);

  useEffect(() => {
    const selectedLocations = JSON.parse(
      sessionStorage.getItem('scout-selected-locations') ?? '[]',
    );

    if (selectedLocations.length === locationsLength) {
      selectAll.current = true;
    }

    handleSelectedLocationIds(selectedLocations);
  }, [handleSelectedLocationIds]);

  useEffect(() => {
    if (isActiveView) {
      const params = {
        sortKey,
        sortOrder,
        search: filters.search,
        page: pageIndex > 0 ? (pageIndex + 1).toString() : '', // this one offset is decorative to offset the 0-based index
      };

      const urlQuery = buildUrlParamString(params);

      Router.replace(`/scout/list${urlQuery}`, undefined, {
        shallow: true,
      });
    }
  }, [sortKey, sortOrder, filters.search, pageIndex, isActiveView]);

  const columns: ColumnDef<ScoutLocation, string>[] = [
    {
      accessorKey: 'checkbox',
      header: (
        <Checkbox
          data-selected={selectAll.current}
          onClick={handleSelectAll}
          className="mr-4 group-hover:visible data-[selected=true]:visible xl:invisible"
        />
      ) as any,
      enableSorting: false,
      cell: ({ row }) => {
        const selected = selectedLocationIds.includes(row.original._id);
        const bursts: Burst[] = row.getValue('bursts') ?? [];
        const latest = bursts[bursts.length - 1];

        return (
          <div className="flex items-center">
            <Checkbox
              disabled={latest?.status === BurstStatus.ACTIVE}
              data-selected={selected}
              onClick={e => {
                setSelectedLocationIds(prevState => {
                  if (prevState.includes(row.original._id)) {
                    const ids = prevState.filter(id => id !== row.original._id);
                    updateSessionStorage(ids);
                    return ids;
                  }
                  const ids = [...prevState, row.original._id];
                  updateSessionStorage(ids);
                  return ids;
                });

                e.stopPropagation();
              }}
              checked={selected}
              className="group-hover:visible data-[selected=true]:visible xl:invisible"
            />
          </div>
        );
      },
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        return <div className="text-xs md:text-sm">{row.getValue('name')}</div>;
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <div className="truncate text-xs md:text-sm">
          {row.getValue('description')}
        </div>
      ),
    },
    {
      accessorKey: 'lastSeen',
      header: 'Last Seen',
      cell: ({ row }) => (
        <div className="text-xs md:text-sm">
          {prettyDate(row.getValue('lastSeen'))}
        </div>
      ),
      // @ts-ignore
      sortingFn: 'customSortingFn',
    },
    {
      accessorKey: 'bursts',
      header: 'Burst Status',
      cell: ({ row }) => {
        const bursts: Burst[] = row.getValue('bursts') ?? [];
        if (bursts.length < 1) {
          return <div className="text-xs md:text-sm">None</div>;
        } else {
          const latest = bursts[bursts.length - 1];
          const isActive = latest.status === BurstStatus.ACTIVE;
          const isExpired = latest.status === BurstStatus.EXPIRED;
          const expiry = DateTime.fromISO(latest.validUntil, {
            zone: 'utc',
          }).toFormat('LLL dd, yyyy');
          return (
            <div
              data-active={isActive}
              className="text-xs data-[active=true]:text-harvestGoldWarning-600 md:text-sm">
              {capitalize(latest.status)}{' '}
              {`${
                isActive ? `- Expires ${expiry}` : isExpired ? `${expiry}` : ''
              }`}
            </div>
          );
        }
      },
    },
  ];

  const table = useReactTable({
    data: locations,
    columns: columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    enableSortingRemoval: false,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex,
        pageSize: PAGE_SIZE,
      },
    },
    initialState: {
      pagination: {
        pageIndex,
        pageSize: PAGE_SIZE,
      },
    },
    sortingFns: {
      customSortingFn: (rowA, rowB) => {
        const aDate = rowA.original.lastSeen
          ? new Date(rowA.original.lastSeen).getTime()
          : 0;
        const bDate = rowB.original.lastSeen
          ? new Date(rowB.original.lastSeen).getTime()
          : 0;
        return bDate - aDate;
      },
    },
  });

  const from = pageIndex * PAGE_SIZE + 1;
  const to =
    from +
    Math.min((pageIndex + 1) * PAGE_SIZE, table.getRowModel().rows.length - 1);
  const total = locations.length;

  return (
    <div className="flex h-[calc(100dvh-248px)] flex-col gap-4 lg:h-[calc(100dvh-215px)]">
      <div className="no-scrollbar h-full flex-1 overflow-scroll rounded-md border [&>div]:overflow-visible">
        <Table className="table-fixed">
          <TableHeader className="sticky top-0 z-10 bg-neutral-800">
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id} className="group">
                {headerGroup.headers.map((header, index) => {
                  return (
                    <TableHead
                      key={header.id}
                      data-order={index}
                      onClick={header.column.getToggleSortingHandler()}
                      className="h-4 w-1/4 p-1.5 data-[order='0']:w-[32px] md:h-12 md:px-4">
                      <div
                        data-order={index}
                        className="flex cursor-pointer items-center gap-1 px-0 text-[10px] md:text-sm">
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {header.column.getIsSorted() === 'asc' ? (
                          <CaretUpIcon />
                        ) : header.column.getIsSorted() === 'desc' ? (
                          <CaretDownIcon />
                        ) : null}
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => {
                const { lastSeen, lastViewed } = row.original;
                const lastSeenTimestamp = lastSeen
                  ? new Date(lastSeen).getTime()
                  : 0;
                const lastViewedTimestamp = lastViewed
                  ? new Date(lastViewed).getTime()
                  : 0;

                return (
                  <TableRow
                    key={row.id}
                    data-new={lastSeenTimestamp > lastViewedTimestamp}
                    className="group cursor-pointer text-neutral-400 hover:bg-neutral-900 data-[new=true]:font-bold data-[new=true]:text-neutral-0"
                    data-state={row.getIsSelected() && 'selected'}>
                    {row.getVisibleCells().map((cell, index) => (
                      <TableCell
                        key={cell.id}
                        className="p-2 md:px-4 md:py-3"
                        onClick={() => {
                          if (index !== 0) {
                            selectionCallback(row.original._id, Views.List);
                          }
                        }}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell
                  colSpan={4}
                  className="p-2 text-xs md:px-4 md:py-3 md:text-sm">
                  No locations found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex w-full items-center justify-end space-x-2 text-neutral-0">
        <div className="flex-1 text-[10px] md:text-xs">
          {total > PAGE_SIZE
            ? `Showing ${from} to ${to} of ${total} locations`
            : `Showing ${total} location${total === 1 ? '' : 's'}`}{' '}
          {selectedLocationIds.length > 0 &&
            `(${selectedLocationIds.length} selected)`}
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            size="sm"
            className="text-[10px] md:text-xs"
            onClick={() => {
              table.previousPage();
              setPageIndex(prev => prev - 1);
            }}
            disabled={!table.getCanPreviousPage()}>
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-[10px] md:text-xs"
            onClick={() => {
              table.nextPage();
              setPageIndex(prev => prev + 1);
            }}
            disabled={!table.getCanNextPage()}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
